import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Head from 'next/head';
import React, { useContext } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { useTextColor } from '../../styles/jss/utils';
import { SiteContext } from '../../utils/common';
import FaqItem from '../FaqItem/FaqItem';
import FeaturedFaqItem, { DEFAULT_SECTION_BACKGROUND, getFeaturedTextColours, isWhiteBackground, MAX_FEATURED_FAQS, pickBackgroundColour, } from '../FeaturedFaqItem/FeaturedFaqItem';
import Link from '../Link/Link';
import useStyles from './FaqGallery.styles';
import { useLoaded } from '../MainContent/useComponentLoading';
/**
 *
 * <h2>Designs</h2>
 * <p>Interflora: https://www.figma.com/file/uriqJDufclCYzXT4yjsFum/Design-Sprint-4?node-id=712%3A39679</p>
 * <p>Flying Flowers: https://www.figma.com/file/sStbIo2VTXH7Tbs27KZU21/FF-Weekly-Outputs?node-id=716%3A60885</p>
 * <p>Featured FAQs: https://www.figma.com/design/YsFmjPuFnDaDAF0rBHSVcg/Design-Sprint-82--18.05.2026-?node-id=52648-16957</p>
 */
const FaqGallery = (props) => {
    const { title, faqItems, onlyLinks, titleTag = 'H2', faqSchema, link, isFeatured, showIcons = true, backgroundColour, } = props;
    const isFeaturedMode = Boolean(isFeatured);
    const { isFlyingFlowers } = useContext(SiteContext);
    const featuredBackground = pickBackgroundColour(backgroundColour, DEFAULT_SECTION_BACKGROUND);
    const { textColor } = useTextColor(featuredBackground);
    const { headingColour } = getFeaturedTextColours(textColor, !!isFlyingFlowers);
    const { classes } = useStyles({
        featuredBackground,
        featuredHeadingColour: headingColour,
        hasFeaturedPadding: !isWhiteBackground(featuredBackground),
    });
    useLoaded(props);
    // Normalize the gallery title tag
    const normalizedTag = (titleTag || '').toLowerCase();
    // Map gallery tag to item tag
    const tagMap = {
        h1: 'H2',
        h2: 'H3',
        h3: 'H4',
        h4: 'H4',
        div: 'H3',
    };
    const questionTag = tagMap[normalizedTag] || 'H3';
    // Items without a question or answer are skipped before the max-4 limit is applied
    const featuredItems = isFeaturedMode
        ? (faqItems !== null && faqItems !== void 0 ? faqItems : []).filter((item) => (item === null || item === void 0 ? void 0 : item.question) && (item === null || item === void 0 ? void 0 : item.answer)).slice(0, MAX_FEATURED_FAQS)
        : [];
    const schemaItems = isFeaturedMode ? featuredItems : faqItems;
    const schemaScript = !!(schemaItems === null || schemaItems === void 0 ? void 0 : schemaItems.length) && faqSchema && (_jsx(Head, { children: _jsx("script", Object.assign({}, jsonLdScriptProps({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: schemaItems === null || schemaItems === void 0 ? void 0 : schemaItems.map((faqItem) => ({
                '@type': 'Question',
                name: faqItem.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faqItem.answer,
                },
            })),
        }))) }));
    if (isFeaturedMode) {
        if (!featuredItems.length)
            return null;
        return (_jsxs("div", { className: classes.featuredRoot, "data-testid": "featured-faq-gallery", children: [title && React.createElement(normalizedTag || 'h2', { className: classes.featuredTitle }, title), _jsx("div", { className: classes.featuredList, children: featuredItems.map((faqItem, index) => (_jsx(FeaturedFaqItem, { question: faqItem.question, answer: faqItem.answer, icon: faqItem.icon, showIcon: showIcons !== false, backgroundColour: faqItem.backgroundColour, anchorKey: faqItem.anchorKey, questionTag: questionTag }, `${faqItem.id}-${index}`))) }), schemaScript] }));
    }
    return (_jsxs("div", { children: [title && React.createElement(normalizedTag || 'h2', { className: classes.title }, title), !!(faqItems === null || faqItems === void 0 ? void 0 : faqItems.length) &&
                faqItems.map((faqItem, index) => (_jsx(FaqItem, { id: faqItem.id, question: faqItem.question, answer: (faqItem === null || faqItem === void 0 ? void 0 : faqItem.answer) && faqItem.answer, onlyLinks: !!onlyLinks, anchorKey: faqItem.anchorKey, questionTag: questionTag }, faqItem.id || `faq-item-${index}`))), (link === null || link === void 0 ? void 0 : link.label) && (_jsx("div", { className: classes.bottomLinkWrapper, children: _jsx(Link, Object.assign({}, link, { className: classes.bottomLink })) })), schemaScript] }));
};
export default FaqGallery;
